import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  Button, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  IconButton, Tooltip, Chip, Box, Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { API_BASE_URL } from "../config";
import ReminderFormDialog from "./ReminderFormDialog";

const REMINDERS_API = `${API_BASE_URL}/reminders`;
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

// A short human description of a reminder's recurrence pattern and progress.
const describe = (r) => {
  if (!r.recurring) return "One-off";
  return `Every ${r.interval_hours}h · ${r.occurrences_sent}/${r.occurrences_total} sent`;
};

/**
 * "My reminders": lists the signed-in user's reminders across all incidents,
 * with edit / delete / open-incident actions.
 *
 * Props: open, onClose, me, onOpenIncident(incidentId)
 */
export default function RemindersDialog({ open, onClose, me, onOpenIncident }) {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [editReminder, setEditReminder] = useState(null); // reminder being edited
  const [confirmDelete, setConfirmDelete] = useState(null); // reminder pending deletion

  const load = useCallback(() => {
    axios.get(REMINDERS_API + "/")
      .then((res) => setRows(res.data))
      .finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    if (open) { setLoaded(false); load(); }
  }, [open, load]);

  const remove = () => {
    axios.delete(`${REMINDERS_API}/${confirmDelete.id}`)
      .then(() => { setConfirmDelete(null); load(); });
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>My reminders</DialogTitle>
        <DialogContent>
          {loaded && rows.length === 0 && (
            <DialogContentText sx={{ py: 2 }}>
              You have no reminders. Open an incident and use “Set a reminder” at the
              bottom of the Other section to create one.
            </DialogContentText>
          )}
          {rows.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Incident</TableCell>
                    <TableCell>Next reminder</TableCell>
                    <TableCell>Pattern</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {r.incident?.patient_name || "—"}
                          {r.incident?.incident_type ? ` · ${r.incident.incident_type}` : ""}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {r.incident?.incident_summary || "(no summary)"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {r.active ? fmtDateTime(r.next_fire_at)
                          : <Chip size="small" label="Finished" />}
                      </TableCell>
                      <TableCell>{describe(r)}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                          <Tooltip title="Open incident">
                            <IconButton size="small"
                                        onClick={() => { onOpenIncident && onOpenIncident(r.incident_id); onClose(); }}>
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit reminder">
                            <IconButton size="small" onClick={() => setEditReminder(r)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete reminder">
                            <IconButton size="small" color="error" onClick={() => setConfirmDelete(r)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit an existing reminder from the list */}
      <ReminderFormDialog
        open={!!editReminder}
        onClose={() => setEditReminder(null)}
        onSaved={load}
        incident={editReminder?.incident}
        reminder={editReminder}
        me={me}
      />

      {/* Delete confirmation */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete reminder?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This reminder will be removed and will not send again. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" startIcon={<DeleteIcon />} onClick={remove}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
